import type { LayoutNode } from "@booro/team";
import { CHILD_GAP, HEADER, LEAD_GAP, PADDING } from "@booro/team";
import type { Edge, EdgeProps } from "@xyflow/react";
import { BaseEdge, getSmoothStepPath } from "@xyflow/react";
import type { CSSProperties } from "react";
import { useLayoutEffect, useRef, useState } from "react";

export const TEAM_HIERARCHY_SOURCE = "team-hierarchy-source";
export const TEAM_HIERARCHY_TARGET = "team-hierarchy-target";

type HierarchyEdgeData = {
  /** How far left of the source the container's spine runs. */
  railOffset?: number;
  /** How far below the source the line turns into the crossbar over the first row. */
  trunkDrop?: number;
};

type HierarchyEdge = Edge<HierarchyEdgeData, "teamHierarchy">;

/** The absolute place of every node, for routing edges and finding drops. */
export function absoluteRects(layout: readonly LayoutNode[]) {
  const byId = new Map(layout.map((node) => [node.id, node]));
  const rects = new Map<string, { x: number; y: number; width: number; height: number }>();
  const resolve = (node: LayoutNode): { x: number; y: number } => {
    const known = rects.get(node.id);
    if (known) return known;
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    const origin = parent ? resolve(parent) : { x: 0, y: 0 };
    const rect = {
      x: origin.x + node.x,
      y: origin.y + node.y,
      width: node.width,
      height: node.height,
    };
    rects.set(node.id, rect);
    return rect;
  };
  for (const node of layout) resolve(node);
  return rects;
}

/**
 * Immediate leadership relations only. A lead branches to everything else in
 * its group or office. Without a lead, everyone in it stands on one level, so
 * no line runs inside it. The Chief leads every office: a line to each office,
 * and with one invisible office, straight to its lead or its root content.
 */
export function buildTeamEdges(layout: readonly LayoutNode[]): HierarchyEdge[] {
  const rects = absoluteRects(layout);
  const children = new Map<string, LayoutNode[]>();
  for (const node of layout) {
    if (!node.parentId) continue;
    const siblings = children.get(node.parentId) ?? [];
    siblings.push(node);
    children.set(node.parentId, siblings);
  }

  const edges: HierarchyEdge[] = [];
  const connect = (source: LayoutNode, target: LayoutNode, container?: LayoutNode) => {
    const containerRect = container ? rects.get(container.id) : undefined;
    const sourceRect = rects.get(source.id);
    let data: HierarchyEdgeData | undefined;
    if (container && containerRect && sourceRect) {
      const sourceY = sourceRect.y + sourceRect.height;
      // The crossbar sits in the gap right above the first row below the source.
      const trunkY =
        source.parentId === container.id
          ? sourceY + LEAD_GAP / 2
          : containerRect.y + HEADER + CHILD_GAP / 2;
      data = {
        railOffset: sourceRect.x + sourceRect.width / 2 - (containerRect.x + PADDING / 2),
        trunkDrop: trunkY - sourceY,
      };
    }
    edges.push({
      id: `hierarchy:${source.id}->${target.id}`,
      source: source.id,
      sourceHandle: TEAM_HIERARCHY_SOURCE,
      target: target.id,
      targetHandle: TEAM_HIERARCHY_TARGET,
      type: "teamHierarchy",
      selectable: false,
      focusable: false,
      className: "team-hierarchy",
      ...(data ? { data } : {}),
    });
  };

  /** The lines inside a group or office; `above` leads it from outside. */
  const branch = (container: LayoutNode, above: LayoutNode | null) => {
    const direct = children.get(container.id) ?? [];
    const lead = direct.find((node) => node.kind === "agent" && node.lead);
    if (lead && above) connect(above, lead, container);
    const from = lead ?? above;
    if (from) {
      for (const child of direct) if (child !== from) connect(from, child, container);
    }
    for (const child of direct) if (child.kind === "group") branch(child, null);
  };

  const offices = layout.filter((node) => node.kind === "office");
  const chief = layout.find((node) => node.kind === "chief") ?? null;
  for (const office of offices) {
    if (offices.length === 1) {
      // The only office has no frame to connect to.
      branch(office, chief);
    } else {
      if (chief) connect(chief, office);
      branch(office, null);
    }
  }
  return edges;
}

type Point = { x: number; y: number };

/** An orthogonal route with small quadratic corners, through the parent's clear gutters. */
function roundedRoute(points: readonly Point[], radius = 5): string {
  const clean = points.filter(
    (point, index) =>
      index === 0 || point.x !== points[index - 1]?.x || point.y !== points[index - 1]?.y,
  );
  const first = clean[0];
  if (!first) return "";
  let path = `M ${first.x} ${first.y}`;
  for (let index = 1; index < clean.length - 1; index += 1) {
    const previous = clean[index - 1];
    const current = clean[index];
    const next = clean[index + 1];
    if (!previous || !current || !next) continue;
    const incoming = Math.hypot(previous.x - current.x, previous.y - current.y);
    const outgoing = Math.hypot(next.x - current.x, next.y - current.y);
    if (!incoming || !outgoing) continue;
    const cut = Math.min(radius, incoming / 2, outgoing / 2);
    const before = {
      x: current.x + ((previous.x - current.x) / incoming) * cut,
      y: current.y + ((previous.y - current.y) / incoming) * cut,
    };
    const after = {
      x: current.x + ((next.x - current.x) / outgoing) * cut,
      y: current.y + ((next.y - current.y) / outgoing) * cut,
    };
    path += ` L ${before.x} ${before.y} Q ${current.x} ${current.y} ${after.x} ${after.y}`;
  }
  const last = clean.at(-1);
  if (last) path += ` L ${last.x} ${last.y}`;
  return path;
}

function HierarchyEdge({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<HierarchyEdge>) {
  let busY = targetY - CHILD_GAP / 2;
  let path: string;
  if (data?.railOffset !== undefined && data.trunkDrop !== undefined) {
    // Down from the source, along the crossbar over the first row, and for
    // lower rows down the container's left spine first.
    const trunkY = sourceY + data.trunkDrop;
    const railX = sourceX - data.railOffset;
    const points: Point[] = [
      { x: sourceX, y: sourceY },
      { x: sourceX, y: trunkY },
    ];
    // The first row hangs straight from the crossbar; rows are far further apart.
    if (Math.abs(busY - trunkY) < LEAD_GAP) busY = trunkY;
    else {
      points.push({ x: railX, y: trunkY }, { x: railX, y: busY });
    }
    points.push({ x: targetX, y: busY }, { x: targetX, y: targetY });
    path = roundedRoute(points);
  } else {
    [path] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      borderRadius: 10,
      offset: 24,
    });
  }
  // Every line of one lead pulses together: the pulse leaves the lead at once
  // on all of them and runs each line from end to end in the same time.
  const phase = [...source].reduce((sum, letter) => sum + letter.charCodeAt(0), 0) % 8;
  const pulse = useRef<SVGPathElement>(null);
  const [length, setLength] = useState(0);
  useLayoutEffect(() => {
    // jsdom has no geometry; the line then simply has no pulse.
    setLength(Math.round(pulse.current?.getTotalLength?.() ?? 0));
  }, [path]);
  return (
    <>
      <BaseEdge id={id} path={path} interactionWidth={0} className="team-hierarchy-edge" />
      <path
        ref={pulse}
        d={path}
        className="team-hierarchy-edge-pulse"
        style={
          {
            "--team-line-length": `${length}px`,
            animationDelay: `${phase * -0.45}s`,
            visibility: length ? undefined : "hidden",
          } as CSSProperties
        }
        aria-hidden
      />
      <circle cx={sourceX} cy={sourceY} r={2.25} className="team-hierarchy-point" aria-hidden />
      <circle cx={targetX} cy={targetY} r={2.25} className="team-hierarchy-point" aria-hidden />
    </>
  );
}

export const TEAM_EDGE_TYPES = { teamHierarchy: HierarchyEdge };
